import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, Loader2, Euro } from "lucide-react";

const ESTADOS = {
  pagado: { label: "Cobrado", cls: "bg-emerald-600 hover:bg-emerald-600 text-white" },
  pendiente: { label: "Pendiente", cls: "bg-amber-500 hover:bg-amber-500 text-white" },
  devuelto: { label: "Devuelto", cls: "bg-destructive hover:bg-destructive text-white" },
  anulado: { label: "Anulado", cls: "bg-muted text-muted-foreground" },
};

/** Todo lo que ha pagado el alumno: suscripciones, pagos únicos, bonos y extras. */
export const HistorialPagos = ({ alumnoId }) => {
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/cobros", { params: { alumno_id: alumnoId } });
      setPagos(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, [alumnoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const descargar = async (id, numero) => {
    try {
      const { data } = await api.get(`/facturas/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${numero.replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar la factura");
    }
  };

  const total = pagos.filter((p) => p.estado === "pagado")
    .reduce((s, p) => s + p.importe, 0);

  return (
    <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="historial-pagos">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-lg font-bold">Historial de pagos</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Suscripciones, pagos únicos, bonos y extras · {total.toFixed(2)} € cobrados
          </p>
        </div>
        <Euro className="h-4 w-4 text-primary" />
      </div>
      {cargando ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : pagos.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="historial-pagos-empty">
          Este alumno todavía no tiene pagos registrados.
        </p>
      ) : (
        <div className="space-y-3">
          {pagos.map((p) => {
            const est = ESTADOS[p.estado] || { label: p.estado, cls: "" };
            return (
              <div key={p.session_id} className="flex flex-wrap items-center gap-3 border-b border-border pb-3 last:border-0 last:pb-0"
                data-testid={`historial-pago-${p.session_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-sm">{p.concepto}</p>
                    <Badge className={`rounded-full ${est.cls}`}>{est.label}</Badge>
                    <Badge variant="outline" className="rounded-full">
                      {p.via === "stripe" ? "Stripe" : "En la academia"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {p.fecha} · {p.tipo_label}
                    {p.factura_numero ? ` · factura ${p.factura_numero}` : ""}
                    {p.estado === "devuelto" ? ` · devuelto ${p.devuelto_importe} €` : ""}
                  </p>
                </div>
                <span className="font-display font-bold">{p.importe} €</span>
                {p.factura_id && (
                  <Button variant="outline" size="sm" className="rounded-full"
                    onClick={() => descargar(p.factura_id, p.factura_numero)}
                    data-testid={`historial-factura-${p.session_id}`}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                )}
                {p.rectificativa_id && (
                  <Button variant="outline" size="sm" className="rounded-full text-destructive"
                    onClick={() => descargar(p.rectificativa_id, p.rectificativa_numero)}
                    data-testid={`historial-rectificativa-${p.session_id}`}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
